import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { RouteComponentProps } from 'react-router-dom';
import { findIndex } from 'lodash-es';
import { Paper, Typography, Button } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMst } from '../../core/stores';
import { Form, getElementByName } from '@jht/react-library';
import { contactPersonFormSchema } from './contact-person-form-schemas';
import {
  productDetailFormSchema,
  contactInformationFormSchema,
  netpulseOrderDetailsFormSchema,
  mobileAppNameSchema,
  desiredAppNameSchema,
} from './product-details-form-schemas';
import { OrderBreadcrumbs } from '../../components/OrderBreadcrumbs/OrderBreadcrumbs';

export interface DOProductDetailsProps extends RouteComponentProps<{ id: string }> {}

const useStyles = makeStyles(theme => ({
  paper: {
    display: 'flex',
    width: '88%',
    flexShrink: 0,
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  formContainer: {
    flexGrow: 1,
    height: '100%',
    margin: theme.spacing(2),
    '& > *': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  summary: {
    margin: 0,
  },
  buttonGroup: {
    width: '100%',
  },
  button: {
    width: '50%',
  },
  title: {
    width: '100%',
  },
  sectionTitle: {
    width: '100%',
    marginTop: 0,
    marginBottom: 0,
  },
  buttomButtonRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  addContactPersonBtn: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  actionButtons: {
    '& > *': {
      marginLeft: theme.spacing(1),
    },
  },
}));

export const ProductDetailsForm: React.FC<DOProductDetailsProps> = observer(({ match }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const orderId = match.params.id;
  const { digitalOrdersStore, navigationStore } = useMst();
  const currentOrder: any = digitalOrdersStore.order(orderId);
  const isWTNProduct = currentOrder.products?.productSelection.COM360;
  const orgName =
    currentOrder.organization.name ||
    digitalOrdersStore.organizationMap.get(currentOrder.organization.organizationId)?.name;
  const [productOrderDetailsForm, setProductOrderDetailsForm] = useState(
    productDetailFormSchema(currentOrder),
  );
  const [contactInfoForm, setContactInfoForm] = useState(
    contactInformationFormSchema(currentOrder),
  );
  const [netpulseOrderDetailsForm, setNetpulseOrderDetailsForm] = useState(
    netpulseOrderDetailsFormSchema(currentOrder, orgName),
  );
  const contactPersonCount = () => {
    return (
      Math.max(
        ...Object.keys(currentOrder.productDetails)
          .filter(key => /contact_/.test(key))
          .map(key => Number(key.charAt(8))),
      ) + 1
    );
  };
  const isContactPersonExistInCurrentOrder = () => {
    return contactPersonSchema.inputNames.some(inputName => {
      const prop = 'contact_0_' + inputName;
      return currentOrder.productDetails && currentOrder.productDetails.hasOwnProperty(prop);
    });
  };
  const contactPersonSchema = {
    inputNames: ['role', 'name', 'email', 'phone'],
    inputLabels: ['Role', 'Name', 'Email Address', 'Phone Number'],
  };
  const [contactPersonForm, setContactPersonForm] = isContactPersonExistInCurrentOrder()
    ? useState(
        contactPersonFormSchema(
          Array(contactPersonCount()).fill(contactPersonSchema),
          currentOrder.productDetails,
        ),
      )
    : useState(null);
  const [contactPerson, setContactPerson] = useState([]);

  const addContactPerson = () => {
    const values: any = {};
    contactPerson.push(contactPersonSchema);
    setContactPerson(contactPerson);
    if (contactPersonForm) {
      contactPersonForm.elements.forEach(element => {
        values[element.name] = element.value;
      });
    }
    setContactPersonForm(contactPersonFormSchema(contactPerson, values));
  };

  const handleContactPersonFormChange = formState => {
    setContactPersonForm(formState);
  };

  const extractedValues = formState => {
    return formState.elements.reduce((memo, curr) => {
      if (curr.value) {
        memo[curr.name] = curr.value;
      }
      return memo;
    }, {});
  };

  const handleProductDetailsFormChange = formState => {
    setProductOrderDetailsForm(formState);
  };

  const handleContactInfoFormChange = formState => {
    setContactInfoForm(formState);
  };

  const isMobileAppNameElementExist = elements => {
    return elements.some(element => element.name === 'mobileAppName');
  };

  const isDesiredMobileAppNameElementExist = elements => {
    return elements.some(element => element.name === 'desiredMobileAppName');
  };

  const handleNetpulseOrderDetailsFormChange = formState => {
    const existingMobileAppElementVal = getElementByName(formState.elements, 'existingMobileApp')
      .value;
    if (existingMobileAppElementVal) {
      const existingMobileAppElementIndex = findIndex(
        formState.elements,
        element => element.name === 'existingMobileApp',
      );
      if (existingMobileAppElementVal === '1') {
        if (isDesiredMobileAppNameElementExist(formState.elements)) {
          formState.elements.splice(
            existingMobileAppElementIndex + 1,
            1,
            mobileAppNameSchema(currentOrder),
          );
        } else {
          if (!isMobileAppNameElementExist(formState.elements)) {
            formState.elements.splice(
              existingMobileAppElementIndex + 1,
              0,
              mobileAppNameSchema(currentOrder),
            );
          }
        }
      } else {
        if (isMobileAppNameElementExist(formState.elements)) {
          formState.elements.splice(
            existingMobileAppElementIndex + 1,
            1,
            desiredAppNameSchema(currentOrder),
          );
        } else {
          if (!isDesiredMobileAppNameElementExist(formState.elements)) {
            formState.elements.splice(
              existingMobileAppElementIndex + 1,
              0,
              desiredAppNameSchema(currentOrder),
            );
          }
        }
      }
    }
    setNetpulseOrderDetailsForm(formState);
  };

  const submit = () => {
    let payload = {
      ...extractedValues(productOrderDetailsForm),
      ...extractedValues(contactInfoForm),
      ...extractedValues(netpulseOrderDetailsForm),
    };
    if (contactPersonForm) {
      payload = { ...payload, ...extractedValues(contactPersonForm) };
    }
    digitalOrdersStore.writeToCurrentOrder(orderId, {
      productDetails: {
        productSelection: currentOrder.products.productSelection,
        ...currentOrder?.productDetails,
        ...payload,
      },
    });
    digitalOrdersStore.submitDigitalOrder(orderId);
    navigationStore.push(`/digital-orders/${orderId}/summary`);
  };

  const back = () => {
    navigationStore.push(`/digital-orders/${orderId}/products`);
  };

  const productIsValid = () =>
    productOrderDetailsForm.elements.some(
      // @ts-ignore
      element => element.required && element.value === '',
    );

  const contactInfoIsValid = () => {
    const emailRegExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return contactInfoForm.elements.some(
      element =>
        // @ts-ignore
        element.required &&
        // @ts-ignore
        ((element.type === 'email' &&
          typeof element.value === 'string' &&
          !element.value.match(emailRegExp)) ||
          element.value === ''),
    );
  };

  const disabledNextBtn = () => {
    if (!isWTNProduct) {
      return productIsValid() || contactInfoIsValid();
    }
    return (
      productIsValid() ||
      contactInfoIsValid() ||
      // @ts-ignore
      netpulseOrderDetailsForm.elements.some(element => element.required && element.value === '')
    );
  };

  return (
    <Paper className={classes.paper}>
      <div className={classes.formContainer}>
        <Typography className={classes.title} variant="h5">
          Product Order Details
        </Typography>
        {productOrderDetailsForm && (
          <>
            <Form
              onChange={handleProductDetailsFormChange}
              onSubmit={() => {}}
              schema={productOrderDetailsForm}
            >
              <></>
            </Form>
          </>
        )}
        <Typography className={classes.title} variant="h5">
          Contact Information
        </Typography>
        {contactInfoForm && (
          <>
            <Form
              onChange={handleContactInfoFormChange}
              onSubmit={() => {}}
              schema={contactInfoForm}
            >
              <></>
            </Form>
          </>
        )}
        <Typography variant="h6">Additional Contacts</Typography>
        {contactPersonForm && (
          <>
            <Form
              onChange={handleContactPersonFormChange}
              onSubmit={() => {}}
              schema={contactPersonForm}
            >
              <></>
            </Form>
          </>
        )}
        <div className={classes.addContactPersonBtn}>
          <Button variant="contained" color="primary" onClick={addContactPerson}>
            Add Additional Contact
          </Button>
        </div>
        {isWTNProduct && (
          <>
            <Typography className={classes.title} variant="h5">
              EGYM Order Details
            </Typography>
            {netpulseOrderDetailsForm && (
              <>
                <Form
                  onChange={handleNetpulseOrderDetailsFormChange}
                  onSubmit={() => {}}
                  schema={netpulseOrderDetailsForm}
                >
                  <></>
                </Form>
              </>
            )}
          </>
        )}
        <div className={classes.buttomButtonRow}>
          <Button variant="contained" color="secondary" onClick={back}>
            Back
          </Button>
          <Button variant="contained" color="primary" onClick={submit} disabled={disabledNextBtn()}>
            Next
          </Button>
        </div>
      </div>
      <OrderBreadcrumbs className={classes.summary} orderId={orderId} />
    </Paper>
  );
});
